import { useEffect, useRef } from "react";

interface ScrollLockOptions {
  enabled: boolean;
}

export const useScrollLock = ({ enabled }: ScrollLockOptions) => {
  const scrollPositionRef = useRef<number>(0);
  const isLockedRef = useRef<boolean>(false);

  useEffect(() => {
    if (enabled) {
      // Lock scroll when enabled
      if (!isLockedRef.current) {
        // Save current scroll position
        scrollPositionRef.current =
          window.scrollY || document.documentElement.scrollTop;

        // Lock body scroll
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollPositionRef.current}px`;
        document.body.style.width = "100%";
        isLockedRef.current = true;
      }
    } else {
      // Restore scroll when lock is disabled
      if (isLockedRef.current) {
        const scrollPosition = scrollPositionRef.current;

        // Save current scroll-behavior to restore later
        const originalScrollBehavior =
          document.documentElement.style.scrollBehavior;

        // Temporarily disable smooth scrolling to prevent visual jump
        document.documentElement.style.scrollBehavior = "auto";

        // Remove all lock styles
        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("position");
        document.body.style.removeProperty("top");
        document.body.style.removeProperty("width");

        // Ensure pointer events work
        document.body.style.removeProperty("pointer-events");

        isLockedRef.current = false;

        // Restore scroll position immediately without animation
        window.scrollTo(0, scrollPosition);

        // Restore original scroll-behavior after a brief delay
        requestAnimationFrame(() => {
          if (originalScrollBehavior) {
            document.documentElement.style.scrollBehavior =
              originalScrollBehavior;
          } else {
            document.documentElement.style.removeProperty("scroll-behavior");
          }
        });
      }
    }

    // Cleanup function - ensure scroll is restored on unmount
    return () => {
      if (isLockedRef.current) {
        const scrollPosition = scrollPositionRef.current;

        // Temporarily disable smooth scrolling
        document.documentElement.style.scrollBehavior = "auto";

        document.body.style.removeProperty("overflow");
        document.body.style.removeProperty("position");
        document.body.style.removeProperty("top");
        document.body.style.removeProperty("width");
        document.body.style.removeProperty("pointer-events");
        isLockedRef.current = false;

        // Restore scroll instantly
        window.scrollTo(0, scrollPosition);

        // Clean up scroll-behavior
        document.documentElement.style.removeProperty("scroll-behavior");
      }
    };
  }, [enabled]);

  return null;
};
